/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.handleClick.bind(this));
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Handles 'click' events on the hotspot images.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.hotspot__btn')) return;
      const hotspot = evt.target.parentNode;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt);
      } else {
        if (this.activeHotspot) this.closeActiveHotspot(evt);
        this.activateHotspot(hotspot);
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleClickOutside(evt) {
      if (evt.target.matches('.hotspot__btn') || evt.target.matches('.hotspot__card') || evt.target.closest('.hotspot__card')) return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Handles 'keyup' events on the document.
     * @param {object} evt - Event object.
     */
    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    /**
     * Handles 'transitionend' events on the shoppable image.
     * @param {object} evt - Event object.
     */
    handleTransitionEnd(evt) {
      if (this.activeHotspot || evt.propertyName !== 'opacity') return;

      this.classList.add('overflow-hidden');
      this.activeCard.style = null;
      this.activeCard = null;

      this.removeEventListener('transitionend', this.transitionHandler);
    }

    /**
     * Activates a hotspot and shows the corresponding product card.
     * @param {Element} hotspot - Selected hotspot.
     */
    activateHotspot(hotspot) {
      this.activeCard = hotspot.querySelector('.hotspot__card');

      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        // Portal card to body to escape transform/stacking contexts from ancestors
        this._cardOriginalParent = this.activeCard.parentNode;
        this.activeCard.removeAttribute('style');
        document.body.appendChild(this.activeCard);
        // Trigger slide-up after next paint
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.activeCard.classList.add('is-mobile-active');
          });
        });
      } else {
        this.checkCardPosition('internal');
      }

      this.classList.remove('overflow-hidden');
      this.setHotspotActiveState(hotspot, true);
      trapFocus(hotspot);

      this.clickOutsideHandler = this.clickOutsideHandler || this.handleClickOutside.bind(this);
      this.keyUpHandler = this.handleKeyup || this.handleKeyup.bind(this);
      this.resizeHandler = this.resizeHandler || this.closeActiveHotspot.bind(this);

      document.addEventListener('click', this.clickOutsideHandler);
      document.addEventListener('keyup', this.keyUpHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);

      if (isMobile) {
        this.showOverlay();
      }
    }

    /**
     * Hides the product card and deactivates the hotspot.
     * @param {object} evt - Event object.
     */
    closeActiveHotspot(evt) {
      if (this.activeHotspot) {
        this.setHotspotActiveState(this.activeHotspot, false);
        removeTrapFocus();
      }

      // Mobile: slide card back down, then restore to original DOM position
      if (this._cardOriginalParent && this.activeCard) {
        const card = this.activeCard;
        const originalParent = this._cardOriginalParent;
        this._cardOriginalParent = null;

        card.classList.remove('is-mobile-active');

        const onSlideOut = () => {
          card.removeEventListener('transitionend', onSlideOut);
          // Move card back to original hotspot
          originalParent.appendChild(card);
        };
        card.addEventListener('transitionend', onSlideOut);
      } else if (evt.type !== 'on:debounced-resize') {
        // Desktop: original transition handling
        this.transitionHandler = this.transitionHandler || this.handleTransitionEnd.bind(this);
        this.addEventListener('transitionend', this.transitionHandler);
      }

      if (evt.type === 'on:debounced-resize') {
        this.classList.add('overflow-hidden');
        if (this.activeCard) {
          this.activeCard.removeAttribute('style');
          this.activeCard = null;
        }
      }

      this.hideOverlay();

      document.removeEventListener('click', this.clickOutsideHandler);
      document.removeEventListener('keyup', this.keyUpHandler);
    }

    /**
     * Ensures the card is within the image bounds horizontally and viewport bounds vertically.
     */
    checkCardPosition(scope = 'global') {
      const cardRect = this.activeCard.getBoundingClientRect();
      const imageRect = this.getBoundingClientRect();

      let bottomBoundary;
      if (scope === 'internal') {
        const section = this.closest('section') || this.closest('.shopify-section');
        bottomBoundary = section ? section.getBoundingClientRect().bottom : document.documentElement.clientHeight;
      } else {
        bottomBoundary = document.documentElement.clientHeight;
      }

      if (cardRect.bottom > bottomBoundary) {
        // Card is out of the viewport to the bottom.
        this.activeCard.style.top = 'auto';
        this.activeCard.style.bottom = 'calc(100% + 12px)';
      }
      if (scope === 'internal') {
        if (cardRect.right >= imageRect.right) {
          // Card is out of the image to the right.
          this.activeCard.style.left = `calc(50% + ${(cardRect.right - imageRect.right) + 5}px)`;
        } else if (cardRect.left <= imageRect.left) {
          // Card is out of the image to the left.
          this.activeCard.style.left = `calc(50% + ${(imageRect.left - cardRect.left) + 5}px)`;
        }
      } else {
        if (theme.mediaMatches.md && cardRect.right > window.innerWidth - 20) {
          // Card is with 20px of the right edge of the viewport on desktop
          this.activeCard.style.left = `calc(50% - ${cardRect.right - window.innerWidth + 45}px)`;
        } else if (theme.mediaMatches.md && cardRect.left < 20) {
          // Card with 20px of the left edge of the viewport on desktop
          this.activeCard.style.left = `calc(50% - ${cardRect.left - 35}px)`;
        } else if (!theme.mediaMatches.md && cardRect.right > imageRect.right) {
          // Card is out of the image to the right on mobile
          this.activeCard.style.left = `calc(50% - ${cardRect.right - imageRect.width + 35}px)`;
        } else if (!theme.mediaMatches.md && cardRect.left < imageRect.left) {
          // Card is out of the image to the left on mobile
          this.activeCard.style.left = `calc(50% - ${cardRect.left - 35}px)`;
        }
      }
    }

    /**
     * Sets the active state of a hotspot.
     * @param {Element} hotspot - Hotspot element.
     * @param {boolean} active - Set the hotspot as active.
     */
    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle('is-active', active);
      hotspot.querySelector('.hotspot__btn').setAttribute('aria-expanded', active);

      // Card may be portaled to body on mobile, use this.activeCard reference directly
      const card = this.activeCard || hotspot.querySelector('.hotspot__card');
      if (card) card.setAttribute('aria-hidden', !active);

      this.activeHotspot = active ? hotspot : null;
    }

    /**
     * Shows the mobile overlay backdrop.
     */
    showOverlay() {
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'hotspot-overlay';
        this.overlayClickHandler = this.closeActiveHotspot.bind(this);
        this.overlay.addEventListener('click', this.overlayClickHandler);
        document.body.appendChild(this.overlay);
      }
      // Force reflow so transition plays
      this.overlay.classList.add('is-visible');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.overlay.classList.add('is-active');
          document.body.classList.add('is-hotspot-image-showing');
        });
      });
    }

    /**
     * Hides and removes the mobile overlay backdrop.
     */
    hideOverlay() {
      if (!this.overlay) return;
      this.overlay.classList.remove('is-active');
      document.body.classList.remove('is-hotspot-image-showing');
      const onEnd = () => {
        this.overlay.classList.remove('is-visible');
        this.overlay.removeEventListener('transitionend', onEnd);
      };
      this.overlay.addEventListener('transitionend', onEnd);
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}
